import { useEffect } from 'react'

import { fetchPropertyRates } from 'actions/PropertyActions'
import type { OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_BED_BANK } from 'constants/offer'
import { EmptyArray } from 'lib/array/arrayUtils'
import buildPropertyRatesKey from 'lib/offer/buildPropertyRatesKey'

import usePropertyOfferMapping from './Offers/usePropertyOfferMapping'
import { useAppDispatch, useAppSelector } from './reduxHooks'

interface PropertyRatesHookParams {
  offerType: typeof OFFER_TYPE_ALWAYS_ON | typeof OFFER_TYPE_BED_BANK
  offerId: string | undefined
  checkIn: string | undefined
  checkOut: string | undefined
  occupancies: Array<App.Occupants>
  utmContent: string | undefined
  roomTypeId: string | undefined
  roomRateId: string | undefined
  disabled?: boolean
}

function usePropertyRates({
  offerType,
  offerId,
  checkIn,
  checkOut,
  occupancies,
  utmContent,
  roomTypeId,
  roomRateId,
  disabled = false,
}: PropertyRatesHookParams): [Array<App.PropertyRoomRates>, boolean, string | undefined] {
  const dispatch = useAppDispatch()

  const [mapping] = usePropertyOfferMapping(offerType, offerId)
  const propertyId = mapping?.propertyId
  const key = buildPropertyRatesKey({
    propertyId,
    checkIn,
    checkOut,
    occupancies,
    utmContent,
    roomTypeId,
    roomRateId,
  })
  const record = useAppSelector(state => state.offer.propertyRates[key])

  const isFetchRequired = !!key && !disabled && !record
  useEffect(() => {
    if (isFetchRequired) {
      dispatch(fetchPropertyRates(propertyId!, {
        checkIn: checkIn!,
        checkOut: checkOut!,
        occupancies,
        utmContent,
        roomTypeId,
        roomRateId,
      }))
    }
    // NB: `key` effectively (validates and) incorporates `propertyId`, `checkIn`, `checkOut`,
    // `occupancies`, `utmContent`, `roomTypeId`, and `roomRateId`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, isFetchRequired, key])

  if (disabled) return [EmptyArray, false, undefined]
  return [
    record?.rooms ?? EmptyArray,
    record?.fetching ?? isFetchRequired,
    record?.error,
  ]
}

export default usePropertyRates
